import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { useMemo } from "react";

import { usePositionsConstants } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { getBorrowingFeeRateUsd, getFundingFeeRateUsd } from "domain/synthetics/fees";
import {
  PositionInfo,
  formatEstimatedLiquidationTime,
  formatLeverage,
  formatLiquidationPrice,
  getEstimatedLiquidationTimeInHours,
} from "domain/synthetics/positions";
import { CHART_PERIODS } from "lib/legacy";
import { formatBalanceAmount, formatDeltaUsd, formatUsd } from "lib/numbers";
import { getPositiveOrNegativeClass } from "lib/utils";
import { formatCngnPrice } from "sdk/utils/cngnCurrency";

import Modal from "components/Modal/Modal";
import {
  formatTradeActionTimestamp,
  formatTradeActionTimestampISO,
} from "components/Synthetics/TradeHistory/TradeHistoryRow/utils/shared";

import { SyntheticsInfoRow } from "../SyntheticsInfoRow";

type Props = {
  isVisible: boolean;
  onClose: () => void;
  position?: PositionInfo;
};

export function PositionTradeDetailsModal({ isVisible, onClose, position }: Props) {
  const { minCollateralUsd } = usePositionsConstants();

  const formatDisplayPrice = (price?: bigint) => {
    if (price === undefined) {
      return "-";
    }

    return (
      formatCngnPrice(price, position?.indexToken.symbol, {
        visualMultiplier: position?.indexToken.visualMultiplier,
      }) ??
      formatUsd(price, {
        visualMultiplier: position?.indexToken.visualMultiplier,
      })
    );
  };

  const { borrowingFeeRateUsd, fundingFeeRateUsd } = useMemo(() => {
    if (!position?.marketInfo) {
      return {
        borrowingFeeRateUsd: undefined,
        fundingFeeRateUsd: undefined,
      };
    }

    return {
      fundingFeeRateUsd: getFundingFeeRateUsd(
        position.marketInfo,
        position.isLong,
        position.sizeInUsd,
        CHART_PERIODS["1d"]
      ),
      borrowingFeeRateUsd: getBorrowingFeeRateUsd(
        position.marketInfo,
        position.isLong,
        position.sizeInUsd,
        CHART_PERIODS["1d"]
      ),
    };
  }, [position]);

  const estimatedLiquidationHours = useMemo(() => {
    if (!position) {
      return undefined;
    }

    return getEstimatedLiquidationTimeInHours(position, minCollateralUsd);
  }, [minCollateralUsd, position]);

  const executedAt = useMemo(() => {
    const timestamp = Number(position?.increasedAtTime ?? 0n);

    if (!timestamp) {
      return undefined;
    }

    return {
      text: formatTradeActionTimestamp(timestamp, false),
      iso: formatTradeActionTimestampISO(timestamp),
    };
  }, [position?.increasedAtTime]);

  if (!position) {
    return null;
  }

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={onClose}
      label={t`Trade Details`}
      description={t`Read-only details for the currently open position.`}
      qa="position-trade-details-modal"
      contentClassName="PositionTradeDetailsModal !w-[min(96vw,720px)] !max-w-[720px]"
    >
      <div className="flex flex-col gap-20">
        <div className="rounded-8 bg-[#111111] p-16">
          <div className="mb-12 flex items-center justify-between gap-12">
            <div className="flex flex-col gap-4">
              <span className="text-18 font-medium text-white">{position.indexName}</span>
              <span className="text-14 font-medium text-gray-lighter">
                {position.poolName ? `${position.poolName} • ` : ""}
                {position.isLong ? t`Long` : t`Short`}
              </span>
            </div>
            <span className="text-16 font-medium text-white">
              {formatLeverage(position.leverageWithPnl ?? position.leverage)}
            </span>
          </div>

          <div className="grid gap-12 md:grid-cols-2">
            <SyntheticsInfoRow label={t`Size`} value={formatUsd(position.sizeInUsd)} />
            <SyntheticsInfoRow
              label={t`Collateral`}
              value={formatBalanceAmount(
                position.remainingCollateralAmount,
                position.collateralToken.decimals,
                position.collateralToken.symbol,
                {
                  isStable: position.collateralToken.isStable,
                }
              )}
            />
            <SyntheticsInfoRow label={t`Net Value`} value={formatUsd(position.netValue)} />
            <SyntheticsInfoRow
              label={t`PnL After Fees`}
              value={formatDeltaUsd(position.pnlAfterFees, position.pnlAfterFeesPercentage)}
              valueClassName={getPositiveOrNegativeClass(position.pnlAfterFees)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-12 rounded-8 bg-[#111111] p-16">
          <span className="text-14 font-medium uppercase tracking-wide text-gray-lighter">
            <Trans>Execution Details</Trans>
          </span>

          <SyntheticsInfoRow label={t`Entry Price`} value={formatDisplayPrice(position.entryPrice)} />
          {executedAt && (
            <SyntheticsInfoRow
              label={t`Executed At`}
              value={
                <time dateTime={executedAt.iso} title={executedAt.iso}>
                  {executedAt.text}
                </time>
              }
            />
          )}
          <SyntheticsInfoRow label={t`Mark Price`} value={formatDisplayPrice(position.markPrice)} />
          <SyntheticsInfoRow
            label={t`Liq. Price`}
            value={
              position.liquidationPrice !== undefined && position.liquidationPrice >= 0
                ? formatDisplayPrice(position.liquidationPrice)
                : formatLiquidationPrice(position.liquidationPrice, {
                    visualMultiplier: position.indexToken.visualMultiplier,
                  })
            }
          />
          {estimatedLiquidationHours !== undefined && (
            <SyntheticsInfoRow
              label={t`Est. Time to Liq.`}
              value={formatEstimatedLiquidationTime(estimatedLiquidationHours)}
            />
          )}
          <div className="h-1 bg-stroke-primary" />
          <SyntheticsInfoRow label={t`Accrued Borrow Fee`} value={formatUsd(-position.pendingBorrowingFeesUsd)} />
          <SyntheticsInfoRow
            label={t`Accrued Funding Fee`}
            value={formatDeltaUsd(position.pendingClaimableFundingFeesUsd - position.pendingFundingFeesUsd)}
            valueClassName={cx({
              "text-primary-alpha-darker": position.pendingClaimableFundingFeesUsd > position.pendingFundingFeesUsd,
              "text-red-alpha-100": position.pendingFundingFeesUsd > position.pendingClaimableFundingFeesUsd,
            })}
          />
          <SyntheticsInfoRow
            label={t`Borrow Fee / Day`}
            value={borrowingFeeRateUsd !== undefined ? formatUsd(-borrowingFeeRateUsd) : "-"}
          />
          <SyntheticsInfoRow
            label={t`Funding Fee / Day`}
            value={fundingFeeRateUsd !== undefined ? formatDeltaUsd(fundingFeeRateUsd) : "-"}
            valueClassName={getPositiveOrNegativeClass(fundingFeeRateUsd)}
          />
        </div>
      </div>
    </Modal>
  );
}
